import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { useGraphiQL, ChevronDownIcon, MethodPill, PanelHeader } from "@graphiql/react";
import { OperationTypeNode } from "graphql";
import { useState, useEffect } from "react";
import { countSelectedFields } from "../lib/schema-walk.js";
import { FragmentSection } from "./fragment-section.js";
import { FragmentEditor } from "./fragment-editor.js";
import { FieldTree } from "./field-tree.js";
import { useWorkingDocument } from "./use-working-document.js";
/* empty css           */
const QueryBuilder = () => {
  const $ = c(40);
  const schema = useGraphiQL(_temp);
  const operationName = useGraphiQL(_temp2);
  const {
    workingDoc,
    activeOpKind,
    target,
    cursorPath,
    handleFocusFragment,
    handleBackToQuery,
    handleToggle,
    handleSetArg,
    handlePromoteArg,
    handleDemoteArg,
    handleAddInlineFragment,
    handleRemoveInlineFragment,
    handleRemoveFragmentSpread,
    handleExtractFragment,
    handleRenameFragment,
    handleDeleteFragment
  } = useWorkingDocument();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [manualExpanded, setManualExpanded] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setManualExpanded({});
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeOpKind) {
    t2 = [activeOpKind];
    $[2] = activeOpKind;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /* @__PURE__ */ jsx(PanelHeader, { title: "Query builder", subtitle: "Tick fields to add them. Edits flow both ways with the editor." });
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const header = t3;
  if (!schema) {
    let t42;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = /* @__PURE__ */ jsxs("div", { className: "graphiql-query-builder", children: [
        header,
        /* @__PURE__ */ jsx("p", { className: "graphiql-qb-empty", children: "No schema loaded." })
      ] });
      $[5] = t42;
    } else {
      t42 = $[5];
    }
    return t42;
  }
  let t4;
  if ($[6] !== schema) {
    t4 = [OperationTypeNode.QUERY, OperationTypeNode.MUTATION, OperationTypeNode.SUBSCRIPTION].map((op) => ({
      op,
      type: schema.getRootType(op)
    })).filter(_temp3);
    $[6] = schema;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const rootTypes = t4;
  let t5;
  if ($[8] !== operationName || $[9] !== workingDoc) {
    t5 = countSelectedFields(workingDoc, operationName);
    $[8] = operationName;
    $[9] = workingDoc;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const selectedCount = t5;
  let t6;
  if ($[11] !== activeOpKind || $[12] !== cursorPath || $[13] !== handleAddInlineFragment || $[14] !== handleBackToQuery || $[15] !== handleDemoteArg || $[16] !== handleExtractFragment || $[17] !== handleFocusFragment || $[18] !== handlePromoteArg || $[19] !== handleRemoveFragmentSpread || $[20] !== handleRemoveInlineFragment || $[21] !== handleRenameFragment || $[22] !== handleSetArg || $[23] !== handleToggle || $[24] !== manualExpanded || $[25] !== rootTypes || $[26] !== schema || $[27] !== selectedCount || $[28] !== target || $[29] !== workingDoc) {
    t6 = target.kind === "fragment" ? /* @__PURE__ */ jsx(FragmentEditor, { fragmentName: target.name, doc: workingDoc, schema, target, cursorPath, onBack: handleBackToQuery, onToggle: handleToggle, onSetArg: handleSetArg, onAddInlineFragment: handleAddInlineFragment, onRemoveInlineFragment: handleRemoveInlineFragment, onRemoveFragmentSpread: handleRemoveFragmentSpread, onExtractFragment: handleExtractFragment, onRenameFragment: handleRenameFragment, onFocusFragment: handleFocusFragment }) : rootTypes.map((t72) => {
      const {
        op: opKind,
        type: rootType
      } = t72;
      const isActiveRoot = opKind === activeOpKind;
      const expanded = manualExpanded[opKind] ?? isActiveRoot;
      return /* @__PURE__ */ jsxs("section", { className: "graphiql-qb-root-section", "data-active": isActiveRoot ? "true" : "false", children: [
        /* @__PURE__ */ jsxs("button", { type: "button", className: "graphiql-qb-op-header", disabled: !isActiveRoot, "aria-expanded": expanded, title: isActiveRoot ? void 0 : `Move the cursor into a ${opKind} operation to edit ${rootType.name} fields`, onClick: () => setManualExpanded((prev) => ({
          ...prev,
          [opKind]: !expanded
        })), children: [
          /* @__PURE__ */ jsx("span", { className: expanded ? "graphiql-qb-chevron-expanded" : "graphiql-qb-chevron-collapsed", children: /* @__PURE__ */ jsx(ChevronDownIcon, {}) }),
          /* @__PURE__ */ jsx(MethodPill, { operation: opKind }),
          /* @__PURE__ */ jsx("span", { className: "graphiql-qb-op-name graphiql-qb-root-name", children: rootType.name }),
          isActiveRoot && /* @__PURE__ */ jsxs("span", { className: "graphiql-qb-op-count", children: [
            selectedCount,
            " selected"
          ] })
        ] }),
        expanded && /* @__PURE__ */ jsx(FieldTree, { type: rootType, path: [], doc: workingDoc, schema: schema ?? void 0, target, cursorPath: isActiveRoot ? cursorPath : void 0, onToggle: handleToggle, onSetArg: handleSetArg, onPromoteArg: handlePromoteArg, onDemoteArg: handleDemoteArg, onAddInlineFragment: handleAddInlineFragment, onRemoveInlineFragment: handleRemoveInlineFragment, onRemoveFragmentSpread: handleRemoveFragmentSpread, onExtractFragment: handleExtractFragment, onRenameFragment: handleRenameFragment, onFocusFragment: handleFocusFragment })
      ] }, rootType.name);
    });
    $[11] = activeOpKind;
    $[12] = cursorPath;
    $[13] = handleAddInlineFragment;
    $[14] = handleBackToQuery;
    $[15] = handleDemoteArg;
    $[16] = handleExtractFragment;
    $[17] = handleFocusFragment;
    $[18] = handlePromoteArg;
    $[19] = handleRemoveFragmentSpread;
    $[20] = handleRemoveInlineFragment;
    $[21] = handleRenameFragment;
    $[22] = handleSetArg;
    $[23] = handleToggle;
    $[24] = manualExpanded;
    $[25] = rootTypes;
    $[26] = schema;
    $[27] = selectedCount;
    $[28] = target;
    $[29] = workingDoc;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const t7 = target.kind === "fragment" ? target.name : void 0;
  let t8;
  if ($[31] !== handleDeleteFragment || $[32] !== handleFocusFragment || $[33] !== handleRenameFragment || $[34] !== t7 || $[35] !== workingDoc) {
    t8 = /* @__PURE__ */ jsx(FragmentSection, { doc: workingDoc, activeFragmentName: t7, onFocusFragment: handleFocusFragment, onRenameFragment: handleRenameFragment, onDeleteFragment: handleDeleteFragment });
    $[31] = handleDeleteFragment;
    $[32] = handleFocusFragment;
    $[33] = handleRenameFragment;
    $[34] = t7;
    $[35] = workingDoc;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t6 || $[38] !== t8) {
    t9 = /* @__PURE__ */ jsxs("div", { className: "graphiql-query-builder", children: [
      header,
      /* @__PURE__ */ jsxs("div", { className: "graphiql-qb-body", children: [
        t6,
        t8
      ] })
    ] });
    $[37] = t6;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};
function _temp(state) {
  return state.schema;
}
function _temp2(state_0) {
  return state_0.operationName;
}
function _temp3(r) {
  return Boolean(r.type);
}
export {
  QueryBuilder
};
//# sourceMappingURL=query-builder.js.map
